import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ArticleHero from "@/components/ArticleHero";
import ArticleSchema from "@/components/ArticleSchema";
import BreadcrumbSchema from "@/components/BreadcrumbSchema";

const PATH = "/how-porcelain-is-made";
const TITLE = "How porcelain is made — a photo essay from the lab bench";
const DESCRIPTION =
  "A long-form, photograph-led explainer on how porcelain veneers are actually fabricated in a dental laboratory — the prescription, the die, the build-up, the firings, and the delivery.";
const PUBLISHED = "2026-04-18";
const MODIFIED = "2026-04-18";

interface PlateProps {
  alt: string;
  caption: string;
}

function Plate({ alt, caption }: PlateProps) {
  return (
    <figure className="my-10 border border-rule bg-paper p-4">
      <div
        className="flex aspect-[3/2] w-full items-center justify-center bg-cream font-sans text-xs uppercase tracking-[0.22em] text-charcoal/60"
        role="img"
        aria-label={alt}
      >
        {/* Placeholder plate — real photograph to be commissioned. */}
        {/* eslint-disable-next-line jsx-a11y/alt-text */}
        <img
          alt={alt}
          aria-hidden="false"
          src="data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 3 2'%3E%3Crect width='3' height='2' fill='%23ece6dc'/%3E%3C/svg%3E"
          className="hidden"
        />
        <span>Plate — to be commissioned</span>
      </div>
      <figcaption className="mt-3 text-small text-charcoal/70">
        {caption} <span className="italic">Photography: to be commissioned.</span>
      </figcaption>
    </figure>
  );
}

export default function HowPorcelainIsMade() {
  return (
    <>
      <SEO title={TITLE} description={DESCRIPTION} path={PATH} type="article" />
      <ArticleSchema
        title={TITLE}
        description={DESCRIPTION}
        path={PATH}
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", path: "/" },
          { name: "Methodology", path: "/methodology" },
          { name: "How porcelain is made", path: PATH },
        ]}
      />

      <ArticleHero
        kicker="Photo essay"
        title="How porcelain is made — a photo essay from the lab bench"
        standfirst="Porcelain veneers are not manufactured. They are built — one layer at a time, by hand, at a bench, over two or three firings. A long look at the craft that sits underneath the aesthetic argument."
        datePublished={PUBLISHED}
        dateModified={MODIFIED}
        readMinutes={15}
      />

      <div className="container-prose prose-editorial mt-10">
        <p>
          A porcelain veneer is roughly half a millimetre thick at its
          thinnest point and weighs almost nothing in the hand. It is the
          visible, polished end of a chain that begins with a dentist's
          preparation and a photograph, passes through a ceramist's bench
          at a dental laboratory, and ends, if everything has gone well,
          invisibly bonded to a tooth in a patient's mouth. This essay is
          about the middle section of that chain — the part the patient
          never sees, the part a clinic rarely photographs, and the part
          that accounts for most of what separates a case that ages
          gracefully from one that looks wrong from the start.
        </p>
        <p>
          The account below describes feldspathic layered porcelain built
          on a refractory die. Most premium anterior cases in Europe are
          still made this way. Pressed lithium-disilicate workflows — the{" "}
          <a
            href="https://www.ivoclar.com/en_li/products/fixed-prosthetics/ips-emax"
            target="_blank"
            rel="noopener noreferrer"
          >
            IPS e.max Press
          </a>{" "}
          family from Ivoclar is the reference point — compress the first
          two steps into a pressed ingot, but the stain-and-glaze stages
          after pressing follow a similar logic. Where a step differs for
          pressed or milled ceramics, the essay says so.
        </p>

        <h2>1. The prescription</h2>
        <p>
          The lab script is the bridge between the dentist's plan and the
          ceramist's bench. A good script includes the material requested,
          the target shade keyed to a physical shade tab, the design notes
          from the digital smile-design session, photographs of the patient
          in ambient light and under flash, a record of the bite, and the
          mock-up if one was fitted. A weaker script includes only the
          material and a shade.
        </p>
        <p>
          This is the point at which the ceramist forms the first opinion
          about how the case will go. A detailed script with full-face
          photographs makes the rest of the work faster and better. A
          script that reads "B1 e.max, 2–10" is a case the ceramist will
          finish competently but rarely beautifully.
        </p>
        <Plate
          alt="A hand-written lab prescription on a clipboard beside a boxed impression and a colour-corrected patient photograph on a light pad."
          caption="The script, the impression, the photograph. Three inputs — together, enough information to start."
        />

        <h2>2. The waxed model</h2>
        <p>
          The first physical artefact at the bench is a stone model of the
          prepared teeth, cast from the impression or printed from the
          intraoral scan. On top of this model, the ceramist builds the
          wax-up — a sculpture in wax of the proposed teeth, to the exact
          shape the finished porcelain will take.
        </p>
        <p>
          The wax-up is where most of the aesthetic decisions already
          live. Width-to-length ratios, proportional relationships between
          central, lateral, and canine, the slope of the incisal edge, the
          depth of the gumline scallop — all of this is resolved in wax
          before any ceramic is touched. A good ceramist will photograph
          the wax-up at eye level and from below, and will send images back
          to the dentist for sign-off before proceeding. Labs that skip
          this step compress a week of aesthetic iteration into a single
          firing, and the compromise is usually visible in the finished
          case.
        </p>
        <Plate
          alt="A stone model on a brass pin with three wax-up teeth, lit from the left; the ceramist's hand is adjusting the incisal edge with a warm instrument."
          caption="The wax-up on the stone model. The shape is committed here — not at the firing."
        />

        <h2>3. The refractory die</h2>
        <p>
          To build a feldspathic veneer that can be fired without
          collapsing, the ceramist needs a heat-resistant support that the
          porcelain can be laid up on directly and survive the kiln. That
          support is the refractory die — a duplicate of the prepared
          tooth, cast from a heat-stable investment material, that goes
          into the furnace with the porcelain on it and comes out intact.
        </p>
        <p>
          The refractory die is a specific, labour-intensive step that
          pressed and milled workflows eliminate. It is one of the reasons
          hand-layered feldspathic cases cost more to produce; it is also
          one of the reasons they look the way they look. The ceramist is
          working directly on a surface the same shape as the patient's
          tooth, at the same scale, under the same loupe.
        </p>
        <Plate
          alt="A row of refractory dies on a ceramist's bench, each seated in an investment collar, with the blue-grey tone characteristic of phosphate-bonded investment materials."
          caption="Refractory dies, ready for the first application of opaque. The colour is a property of the investment, not the eventual ceramic."
        />

        <h2>4. Powder and liquid — the build-up</h2>
        <p>
          Feldspathic porcelain arrives at the bench as a set of fine
          powders — dentine, enamel, incisal, effect shades, translucents —
          mixed on a glass slab with a modelling liquid to a consistency
          between toothpaste and clay. Using fine sable brushes, the
          ceramist applies the powders in sequence: an opaque core to mask
          the underlying preparation shade, a dentine body, an enamel
          layer, an incisal translucent at the biting edge, effect shades
          for characterisation. The build-up is done wet, with the
          ceramist constantly blotting the brush against absorbent paper to
          condense the powder.
        </p>
        <p>
          The build-up is roughly 30% oversized at this stage, because the
          powder contracts substantially in the first firing. This is the
          step at which experience shows most visibly in the finished case.
          Younger ceramists tend to layer defensively and uniformly; senior
          ceramists modulate the layering case-by-case, reading the
          reference photograph and the wax-up on the bench.
        </p>
        <p>
          Manufacturer powder systems vary. The{" "}
          <a
            href="https://www.ivoclar.com/en_li/products/fixed-prosthetics/ips-style"
            target="_blank"
            rel="noopener noreferrer"
          >
            IPS Style Ceram
          </a>{" "}
          layering ceramic, the{" "}
          <a
            href="https://www.gc.dental/europe/en-gb/products/initial"
            target="_blank"
            rel="noopener noreferrer"
          >
            GC Initial
          </a>{" "}
          family, and the{" "}
          <a
            href="https://www.shofu.co.jp/en/product/ceramage/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Shofu Vintage
          </a>{" "}
          range each have their own handling characteristics. A ceramist
          will usually work in one family for decades — switching systems
          is closer to learning a new instrument than to changing a tool.
        </p>
        <Plate
          alt="A ceramist's hand with a fine sable brush applying a translucent enamel layer to a veneer on a refractory die, under a bright task light, with a glass slab of porcelain powders in the foreground."
          caption="Layering — powder, liquid, brush, light. Two hours of work, invisible at five paces."
        />

        <h2>5. The first firing</h2>
        <p>
          The built-up porcelain goes into a programmable dental kiln and
          is fired under vacuum, typically somewhere between 800 and 930
          degrees Celsius depending on the material. The temperature curve
          — how fast the kiln rises, how long it holds, how the vacuum is
          released — is set to the manufacturer's schedule for that
          powder. A ceramist running a modern kiln has the schedule stored
          in memory; a lab without good kiln protocols is a lab with
          inconsistent optical results.
        </p>
        <p>
          What comes out of the first firing is about 20% smaller than
          what went in, fully dense, and, in a good case, already
          recognisable as the tooth it will become. What it lacks is fine
          surface texture and the final edge. Most cases are fired at
          least twice for that reason.
        </p>
        <Plate
          alt="Three veneers on a firing tray inside a ceramic oven, glowing faintly red; a digital temperature display in the foreground reads 910°C."
          caption="First firing. The shape has condensed and the porcelain is dense; texture, edge, and glaze are still to come."
        />

        <h2>6. The second firing (and sometimes a third)</h2>
        <p>
          After the first firing, the ceramist adjusts the shape and adds
          a correction layer — small additions of porcelain where the
          contraction has pulled the margin short, or where the incisal
          translucent needs to be deeper. Internal effect stains are
          placed between the layers in this second build-up: a faint
          halo, a subtle white spot in the enamel, a shadow at the gum
          line. The tooth goes back into the kiln.
        </p>
        <p>
          Some cases require a third firing for a further characterisation
          layer. Every extra firing is a small biological stress on the
          porcelain and is planned, not accidental. Labs that fire five or
          six times to force the shape are labs that have not committed
          to the case on the bench.
        </p>

        <h2>7. Characterisation</h2>
        <p>
          Characterisation is the stage that separates a competent veneer
          from a persuasive one. The ceramist paints fine internal stains
          — browns, ochres, soft greys, occasional whites — into the
          porcelain to reproduce the small irregularities a natural tooth
          carries. A central incisor with no characterisation reads as
          plastic at three feet. A central incisor with a faint horizontal
          enamel band and a subtle incisal halo reads as a tooth.
        </p>
        <p>
          There is a temptation, at this stage, to characterise heavily —
          because heavy characterisation photographs well under macro
          light. On the patient's face, heavy characterisation reads as
          theatrical. The discipline is restraint.
        </p>
        <Plate
          alt="A macro close-up of a single finished veneer on a black velvet pad, with the faint horizontal enamel band and incisal halo visible."
          caption="A characterised central incisor. The effects are small; on a face, they are the difference between real and not."
        />

        <h2>8. Glazing</h2>
        <p>
          The final firing is the glaze — a thin, glossy outer layer that
          seals the porcelain and gives it the light-reflecting quality of
          enamel. The glaze is tuned to the finish the case requires:
          younger patients usually carry a higher gloss, older patients a
          softer matte. This is also where the ceramist decides whether
          the final texture will be smooth or carry subtle surface
          waviness — the so-called perikymata, the fine horizontal
          striations visible on young natural enamel.
        </p>
        <p>
          The glazed veneer goes on a carding board under a bench lamp.
          If the ceramist holds it up against a white background, tilts
          it, and sees the internal colour shift as the light changes,
          the optics are working. If the veneer looks identical from
          every angle, it is too opaque, and it will read as opaque on
          the patient.
        </p>

        <h2>9. Delivery</h2>
        <p>
          The finished case is packed, numbered, and sent back to the
          dentist for the try-in appointment. In a lab where the ceramist
          sees the patient, the ceramist often attends the try-in — to
          watch the veneers on the face in the patient's own lighting,
          adjust internal shade if the light is warmer than the bench
          lamp, refine the incisal edge against the lip line. This is the
          last chance to correct. Once the case is cemented, further
          adjustment means removing material from the finished porcelain
          with a fine diamond, which never recovers the original optics.
        </p>
        <p>
          The whole sequence, from impression to delivery, takes two to
          three weeks at a serious lab for a premium anterior case. It
          can be compressed to five days. It cannot honestly be compressed
          further.
        </p>

        <h2>Why this matters to a patient</h2>
        <p>
          A patient choosing between clinics is, whether they know it or
          not, also choosing between lab bench cultures. A clinic that
          works with a ceramist who layers feldspathic on a refractory die
          and fires twice to characterise is buying a different kind of
          case from a clinic that sends a scan to an offsite milling
          centre and receives monolithic e.max three days later. Both
          workflows have a place. A careful patient simply asks which one
          is being purchased in their case, and why.
        </p>

        <h2>Further reading</h2>
        <p>
          The biology on the clinic side of the workflow is covered in{" "}
          <Link to="/minimal-prep-vs-overprepped-veneers">
            minimal-prep versus over-prepped veneers
          </Link>
          . The visual signatures a well-made veneer leaves on the
          finished smile are the subject of{" "}
          <Link to="/natural-vs-overdone-veneers">
            natural versus overdone veneers
          </Link>
          . For the broader framework a patient can use when evaluating
          clinics, see the{" "}
          <Link to="/how-to-choose-a-smile-makeover-clinic-in-spain">
            decision framework
          </Link>
          .
        </p>

        <h2>References and spec sheets</h2>
        <ul>
          <li>
            <a
              href="https://www.ivoclar.com/en_li/products/fixed-prosthetics/ips-emax"
              target="_blank"
              rel="noopener noreferrer"
            >
              Ivoclar Vivadent — IPS e.max product family
            </a>
            : pressed and CAD lithium-disilicate materials, including
            technical handling parameters.
          </li>
          <li>
            <a
              href="https://www.ivoclar.com/en_li/products/fixed-prosthetics/ips-style"
              target="_blank"
              rel="noopener noreferrer"
            >
              Ivoclar Vivadent — IPS Style Ceram
            </a>
            : layering ceramic system referenced for feldspathic build-up.
          </li>
          <li>
            <a
              href="https://www.gc.dental/europe/en-gb/products/initial"
              target="_blank"
              rel="noopener noreferrer"
            >
              GC Europe — GC Initial
            </a>
            : an alternative layering porcelain family, widely used in
            European labs.
          </li>
          <li>
            <a
              href="https://www.shofu.co.jp/en/product/ceramage/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Shofu — Vintage / Ceramage
            </a>
            : indirect and polymer-ceramic systems used in some hybrid
            cases.
          </li>
        </ul>

        <hr className="rule" />
        <p className="text-small text-charcoal/70">
          By the Editorial team. Published{" "}
          {new Date(PUBLISHED).toLocaleDateString("en-GB", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
          . Last reviewed{" "}
          {new Date(MODIFIED).toLocaleDateString("en-GB", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
          . Images indicated above are placeholder plates; commissioned
          photography is pending.
        </p>
        <div className="mt-10">
          <div className="eyebrow mb-2">Revision log</div>
          <ul className="text-small text-ink/80">
            <li>2026-04-18 — First publication.</li>
          </ul>
        </div>
      </div>
    </>
  );
}
